import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { InputModule } from '@sharedModule/components/input/input.module';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { Subject, takeUntil } from 'rxjs';
import { TransactionSettingsService } from '../../services/transaction-settings.service';
import { MasterTransactionSettingsInterface } from '../../types/transaction-settings';
import { UpsertMasterTransactionSettingsComponent } from '../upsert-master-transaction-settings/upsert-master-transaction-settings.component';

@Component({
  selector: 'app-master-transaction-setting-tabs',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './master-transaction-setting-tabs.component.html',
  styleUrl: './master-transaction-setting-tabs.component.scss',
})
export class MasterTransactionSettingTabsComponent
  implements OnInit, OnDestroy
{
  @Input() selectedTab!: string;
  displayedColumns: string[] = [
    'name',
    'description',
    'is_active',
    'created_at',
    'action',
  ];
  dataSource!: MatTableDataSource<MasterTransactionSettingsInterface>;
  #destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialog,
    private confirmationDialogService: ConfirmationDialogService,
    private transactionSettingsService: TransactionSettingsService
  ) {}

  ngOnInit(): void {
    this.transactionSettingsService.dialogData$
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.loadData();
          this.transactionSettingsService.setDialogData(false);
        }
      });
    this.loadData();
  }

  loadData(): void {
    this.transactionSettingsService
      .getMasterTransactionSettings(this.selectedTab)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.dataSource = new MatTableDataSource(x.response);
        },
      });
  }

  onEdit(data: MasterTransactionSettingsInterface): void {
    const dialogRef = this.dialogRef.open(
      UpsertMasterTransactionSettingsComponent,
      {
        disableClose: true,
        data: {
          isUpdate: true,
          selectedTab: this.selectedTab,
          code: data.code,
          name: data.name,
          description: data.description,
        },
      }
    );
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadData();
    });
  }

  async onDelete(data: MasterTransactionSettingsInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.transactionSettingsService
        .deleteMasterTransactionSetting(this.selectedTab, {
          code: data.code,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) this.loadData();
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
